import React, { useState, useEffect } from "react";
import { Stepper, Step, StepLabel, Box, Card, Theme } from "@material-ui/core";
import { styled, useTheme } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import Typography from "../typography";
import StepInitial from "./StepInitial";
import StepAddToTeam from "./StepAddToTeam";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import { injectIntl } from "react-intl";
import Button from "../button";
import Link from "../link";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

const TEAMS_QUERY = gql`
  query AllTeams {
    allTeams {
      id
      name
    }
  }
`;

export const ADD_PLAYER = gql`
  mutation AddPlayer(
    $name: String!
    $surname: String!
    # $nickName: String!
    $number: Int
    $position: String!
    $dateOfBirth: String!
  ) {
    createPlayer(
      input: {
        name: $name
        surname: $surname
        # nickName: $nickName
        number: $number
        position: $position
        dateOfBirth: $dateOfBirth
      }
    ) {
      id
      name
      surname
      nickName
      number
      position
      dateOfBirth
    }
  }
`;

const PageContainer = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  minHeight: "100vh",
  padding: theme.spacing(3),
  paddingBottom: theme.spacing(6),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
}));

const ContentCard = styled(Card)(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: "18px",
  padding: theme.spacing(4),
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  maxWidth: "700px",
  margin: "0 auto",
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    borderRadius: "16px",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
}));

const StyledStepper = styled(Stepper)(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: "16px",
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    borderRadius: "12px",
  },
  "& .MuiStepLabel-label": {
    marginTop: theme.spacing(1),
    fontWeight: 600,
    fontSize: "0.95rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.75rem",
    },
  },
  "& .MuiStepLabel-label.MuiStepLabel-active": {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  "& .MuiStepLabel-label.MuiStepLabel-completed": {
    color: theme.palette.success.main,
    fontWeight: 600,
  },
  "& .MuiStepIcon-root": {
    color: "rgba(0, 0, 0, 0.12)",
    fontSize: "2rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.75rem",
    },
  },
  "& .MuiStepIcon-root.MuiStepIcon-active": {
    color: theme.palette.primary.main,
    transform: "scale(1.1)",
    transition: "all 0.3s ease",
  },
  "& .MuiStepIcon-root.MuiStepIcon-completed": {
    color: theme.palette.success.main,
  },
  "& .MuiStepConnector-line": {
    borderColor: "rgba(0, 0, 0, 0.12)",
    borderTopWidth: "3px",
  },
  "& .MuiStepConnector-root.MuiStepConnector-completed .MuiStepConnector-line": {
    borderColor: theme.palette.success.main,
  },
}));

const SuccessCard = styled(Box)(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  padding: theme.spacing(4),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const SuccessIcon = styled(Box)(({ theme }: IStyledArguments) => ({
  width: "100px",
  height: "100px",
  borderRadius: "50%",
  background: theme.palette.success.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 24px",
  "& svg": {
    fontSize: "60px",
    color: "white",
  },
}));

const SuccessMessage = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "1.5rem",
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.25rem",
  },
}));

const StyledActionLink = styled(Link)(({ theme }: IStyledArguments) => ({
  textDecoration: "none",
  display: "inline-block",
  "& button": {
    minWidth: "200px",
    padding: theme.spacing(1.5, 4),
    fontSize: "1rem",
    fontWeight: 600,
    borderRadius: "12px",
    background: theme.palette.primary.main,
    transition: "background 0.2s ease",
    "&:hover": {
      background: theme.palette.primary.dark,
    },
  },
}));

const LoadingContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "400px",
  gap: theme.spacing(2),
}));

// interface IRegisterRequest {
//   code: string;
//   firstName: string;
//   lastName: string;
//   language: string;
//   password: string;
// }

// interface IPageRegisterProps {
//   onRegister: (arg0: IRegisterRequest) => null;
//   registrationCode: string;
//   intl: InjectedIntl;
// }

const PageNewPlayer = () => {
  const theme = useTheme<Theme>();
  const [userDetails, setUserDetails] = useState<any>();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [teams, setTeams] = useState<any>(null);
  const { data: dataTeams } = useQuery(TEAMS_QUERY);
  const [addPlayer, { data, loading }] = useMutation(ADD_PLAYER);
  const handleAddPlayer = async (form: any) => {
    addPlayer({
      variables: {
        name: form.name,
        surname: form.surname,
        // nickName: form.nickname,
        number: Number(form.number),
        position: form.position,
        dateOfBirth: form.dateOfBirth,
      },
    });
  };

  useEffect(() => {
    if (loading) {
      return;
    }

    if (dataTeams) {
      setTeams(dataTeams.allTeams);
    }

    if (!data) {
      return;
    } else {
      console.log("data received....");
      console.log(data);
      setUserDetails(data.createPlayer);
      setCurrentStep(2);
    }
  }, [data, dataTeams]);

  if (loading) {
    return (
      <PageContainer>
        <ContentCard>
          <LoadingContainer>
            <PersonAddIcon style={{ fontSize: "80px", color: theme.palette.primary.main }} />
            <SuccessMessage>Creating Player...</SuccessMessage>
          </LoadingContainer>
        </ContentCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentCard>
        <StyledStepper activeStep={currentStep - 1} alternativeLabel>
          <Step>
            <StepLabel>Player Information</StepLabel>
          </Step>
          <Step>
            <StepLabel>Add to Team</StepLabel>
          </Step>
          <Step>
            <StepLabel>Complete</StepLabel>
          </Step>
        </StyledStepper>

        {currentStep === 1 && (
          <StepInitial
            userDetails={userDetails}
            onGoNext={(values: any) => {
              console.log(values);
              handleAddPlayer(values);
            }}
          />
        )}

        {currentStep === 2 && (
          <StepAddToTeam
            userDetails={userDetails}
            teams={teams}
            onGoNext={() => {
              setCurrentStep(3);
            }}
          />
        )}

        {currentStep === 3 && (
          <SuccessCard>
            <SuccessIcon>
              <CheckCircleIcon />
            </SuccessIcon>
            <SuccessMessage>
              Player {userDetails && userDetails.name} {userDetails && userDetails.surname} Successfully Created!
            </SuccessMessage>
            <Box mt={2} mb={3}>
              <Typography.Body style={{ color: "#666", fontSize: "1rem" }}>
                The player has been added to your system and is ready to be assigned to a team.
              </Typography.Body>
            </Box>
            <StyledActionLink to="/people/players">
              <Button>View All Players</Button>
            </StyledActionLink>
          </SuccessCard>
        )}
      </ContentCard>
    </PageContainer>
  );
};

export default injectIntl(PageNewPlayer);
